import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './style.scss';
import StackItemsView from 'components/Common/StackItemsView';
import prices from 'assets/data/lab_prices.json';
import _ from 'lodash';
import DefaultTextField from 'components/Utils/DefaultTextField';
import Icon from 'components/Utils/NewIcon';
import IconButton from '@material-ui/core/IconButton';
import DefaultButton from 'components/Utils/NewDefaultButton';

const processingFee = { ohip: 0, nonOhip: 20 };
const DEFAULT_MANUAL_ENTRY = {
  priceOhip: '0.00',
  priceNonOhip: '0.00',
  name: '',
};

const LabPriceCalculator = ({ onCopy }) => {
  const [stack, setStack] = useState([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [manualEntry, setManualEntry] = useState(DEFAULT_MANUAL_ENTRY);

  useEffect(() => {
    debouncedSearch(search);
  }, [search]);

  const debouncedSearch = useCallback(_.debounce(doSearch, 50), []);

  function doSearch(value) {
    value = value.toLowerCase();
    let results = prices.filter((p) => {
      return JSON.stringify(p).toLowerCase().includes(value);
    });
    setResults(results);
  }

  function clickedItem(item) {
    setStack([...stack, item]);
  }

  function describeStackItem(item) {
    let priceNonOhip = getDisplayPrice(item.priceNonOhip);
    let priceOhip = getDisplayPrice(item.priceOhip);
    return `${item.name} (${priceNonOhip}/${priceOhip})`;
  }

  function deleteStackItem(item) {
    let newStack = [...stack];
    const memberToRemove = newStack.find((element) => element.name === item.name);
    newStack.splice(newStack.indexOf(memberToRemove), 1);
    setStack(newStack);
  }

  const testsTotal = useMemo(() => {
    function getPrice(stack, index) {
      return stack
        .map((r) => parseFloat(r[index]))
        .reduce((accum, floatVal) => {
          if (isNaN(floatVal)) return accum;
          return floatVal + accum;
        }, 0);
    }

    return {
      priceOhip: getPrice(stack, 'priceOhip'),
      priceNonOhip: getPrice(stack, 'priceNonOhip'),
    };
  }, [stack]);

  const addManualEntryDisabled = !manualEntry.name;

  function addManualEntry() {
    clickedItem(manualEntry);
    setManualEntry(DEFAULT_MANUAL_ENTRY);
  }

  const totalOhip = parseFloat(testsTotal.priceOhip + processingFee.ohip).toFixed(2);
  const totalNonOhip = parseFloat(testsTotal.priceNonOhip + processingFee.nonOhip).toFixed(2);

  return (
    <div className="lab-price-calculator">
      <div className="search-content">
        <DefaultTextField
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          name="Search"
          placeholder="Search lab prices"
        />
        <div className=" results">
          {results.length > 0 && (
            <table>
              <thead>
                <tr>
                  <td>Description</td>
                  <td>Non-OHIP</td>
                  <td>OHIP</td>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  return (
                    <tr className="result" onClick={() => clickedItem(r)}>
                      <td className="value">{r.name}</td>
                      <td className="price">{getDisplayPrice(r.priceNonOhip)}</td>
                      <td className="price">{getDisplayPrice(r.priceOhip)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {results.length === 0 && (
            <span className="small-title">No results! Please refine your search.</span>
          )}
        </div>
        <div className="manual-entry">
          <span className="small-title mt-4">Manual Test Entry</span>
          <div className="row">
            <table>
              <tbody>
                <tr className="result">
                  <td className="value">
                    <DefaultTextField
                      value={manualEntry.name}
                      onChange={(event) => {
                        let value = event.target.value;
                        setManualEntry((prevState) => {
                          return {
                            ...prevState,
                            name: value,
                          };
                        });
                      }}
                      label="Test name"
                      placeholder="Type test name"
                    />
                  </td>
                  <td className="price">
                    <DefaultTextField
                      value={manualEntry.priceNonOhip}
                      onChange={(event) => {
                        let value = event.target.value;
                        setManualEntry((prevState) => {
                          return {
                            ...prevState,
                            priceNonOhip: value,
                          };
                        });
                      }}
                      label="Price Non-OHIP"
                    />
                  </td>
                  <td className="price">
                    <DefaultTextField
                      value={manualEntry.priceOhip}
                      onChange={(event) => {
                        let value = event.target.value;
                        setManualEntry((prevState) => {
                          return {
                            ...prevState,
                            priceOhip: value,
                          };
                        });
                      }}
                      label="Price OHIP"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <IconButton
              disabled={addManualEntryDisabled}
              onClick={addManualEntry}
              style={{ width: '40px', height: '40px', top: '10px' }}
            >
              <Icon src="plus" height="20px" width="20px" />
            </IconButton>
          </div>
        </div>
      </div>
      <div className="summary">
        <span className="small-title">Selected Tests</span>
        {stack.length === 0 && <span style={{ marginTop: '1x' }}>No items to show</span>}
        {stack.length > 0 && (
          <StackItemsView
            stack={stack}
            describeItem={describeStackItem}
            onDeleteItem={deleteStackItem}
          />
        )}
        <div className="total">
          <table>
            <thead>
              <th></th>
              <th>Non-OHIP</th>
              <th>OHIP</th>
            </thead>
            <tbody>
              <tr>
                <td>Tests</td>
                <td>${parseFloat(testsTotal.priceNonOhip).toFixed(2)}</td>
                <td>${parseFloat(testsTotal.priceOhip).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Processing Fee</td>
                <td>${parseFloat(processingFee.nonOhip).toFixed(2)}</td>
                <td>${parseFloat(processingFee.ohip).toFixed(2)}</td>
              </tr>
              <tr>
                <td>Total</td>
                <td>${totalNonOhip}</td>
                <td>${totalOhip}</td>
              </tr>
            </tbody>
          </table>

          {!!onCopy && (
            <>
              <DefaultButton
                style={{ width: '100%', margin: '15px auto' }}
                name={`Set Non-OHIP Price ($${totalNonOhip})`}
                icon="times-circle"
                onClick={() => onCopy(totalNonOhip)}
              />
              <DefaultButton
                style={{ width: '100%' }}
                name={`Set OHIP Price ($${totalOhip})`}
                icon="id-card"
                onClick={() => onCopy(totalOhip)}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function getDisplayPrice(price = 0) {
  let floatPrice = parseFloat(price).toFixed(2);
  if (isNaN(floatPrice)) return price;
  return '$' + floatPrice;
}

export default LabPriceCalculator;
